<template>
  <v-card>
    <v-card-title>
      <span class="headline">{{ title }}</span>
    </v-card-title>
    <v-card-text>
      <v-container grid-list-md>
        <v-flex xs12>
          <form>
            <v-select
              label="Distributor"
              v-model="distributor"
              :items="distributors"
              item-text="name"
              item-value="id"
              :error-messages="distributorErrors"
              @change="$v.distributor.$touch()"
              @blur="$v.distributor.$touch()"
              prepend-icon="local_mall"
              autocomplete
              dark
              required
            ></v-select>
            <v-select
              label="SKU"
              v-model="sku"
              :items="skuList"
              item-text="name"
              item-value="id"
              :error-messages="skuErrors"
              @input="$v.sku.$touch()"
              @blur="$v.sku.$touch()"
              prepend-icon="assignment"
              autocomplete
              dark
              required
            ></v-select>
            <v-text-field
              v-if="sku"
              label="App Blocks"
              v-model="blocksCount"
              :error-messages="blocksCountErrors"
              mask="#####"
              @input="$v.count.$touch()"
              @blur="$v.count.$touch()"
              prepend-icon="view_module"
              required
            ></v-text-field>
            <v-text-field
              label="Codes Count"
              v-model="count"
              :error-messages="countErrors"
              mask="#####"
              @input="$v.count.$touch()"
              @blur="$v.count.$touch()"
              prepend-icon="code"
              required
            ></v-text-field>
            <v-text-field
              v-if="name"
              label="Name"
              v-model="name"
              :error-messages="nameErrors"
              :counter="30"
              @input="$v.name.$touch()"
              @blur="$v.name.$touch()"
              prepend-icon="label"
              required
            ></v-text-field>
            <v-text-field
              v-if="distributor"
              label="Order"
              v-model="order"
              :error-messages="orderErrors"
              mask="#####"
              @input="$v.order.$touch()"
              @blur="$v.order.$touch()"
              prepend-icon="list"
              required
            ></v-text-field>
          </form>
        </v-flex>
        <small>*All fields is required</small>
      </v-container>
    </v-card-text>
    <v-card-actions>
      <v-progress-linear indeterminate v-if="loading"></v-progress-linear>
      <v-spacer v-if="!loading"></v-spacer>
      <div v-if="!loading">
        <v-btn color="blue darken-1" flat @click="close">Close</v-btn>
        <v-btn color="blue darken-1" flat @click="save">Submit</v-btn>
      </div>
    </v-card-actions>
    <v-snackbar
      :timeout="3000"
      :bottom="true"
      :right="true"
      :multi-line="false"
      v-model="snackBar"
    >
      {{ snackBarText }}
      <v-btn flat color="pink" @click.native="snackBar = false">Close</v-btn>
    </v-snackbar>
  </v-card>
</template>

<script>
import { validationMixin } from 'vuelidate'
import { required, maxLength, numeric, minValue, maxValue } from 'vuelidate/lib/validators'
import { DISTRIBS_LIST } from '../../store/actions/distributors'
import { SKU_LIST } from '../../store/actions/sku'
import { mapGetters } from 'vuex'
import api from '../../api/distributors'

export default {
  mixins: [validationMixin],
  validations: {
    name: { required, maxLength: maxLength(30) },
    order: { required, numeric, minValue: minValue(1), maxValue: maxValue(20000) },
    distributor: { required },
    blocksCount: { required },
    sku: { required },
    count: { required, numeric, minValue: minValue(1), maxValue: maxValue(20000) }
  },
  props: ['title', 'init', 'distributorInit'],
  data: () => ({
    name: null,
    distributor: null,
    blocksCount: 0,
    sku: null,
    count: '10000',
    snackBarText: 'Input is not valid!',
    snackBar: false,
    isUpdate: false,
    counts: [12, 24, 50, 100, 200, 400, 800, 1600],
    loading: false,
    number: 1,
    order: null
  }),
  methods: {
    save: async function () {
      this.name = this.name.trim()
      this.$v.$touch()
      if (this.$v.$error) {
        this.snackBar = true
        this.snackBarText = 'Input is not valid!'
        return
      }
      this.loading = true
      this.$emit('save', {
        name: this.name,
        distributorId: this.distributor,
        skuId: this.sku,
        blocksCount: this.blocksCount,
        count: parseInt(this.count),
        order: parseInt(this.order)
      })
      this.$v.$reset()
    },
    close: function () {
      this.clear()
      this.$emit('close')
    },
    clear: function () {
      this.$v.$reset()
      this.name = this.init.name || ''
      this.sku = (this.init.sku) ? this.init.sku.id : ''
      this.distributor = (this.init.distributor) ? this.init.distributor.id : (this.distributorInit) ? this.distributorInit : ''
      this.count = this.init.count || '10000'
      this.blocksCount = this.init.blocksCount || ''
      this.isUpdate = true
      this.stopLoading()
    },
    stopLoading: function () {
      this.loading = false
    },
    setName: async function () {
      const distributor = (this.$store.getters.distributorsById(this.distributor))[0]
      const sku = (this.$store.getters.skuById(this.sku))[0]
      if (sku && distributor) {
        this.name = `${distributor.shortName}-${this.order}-${sku.code}-${sku.count}ab-${this.count}`
      }
    }
  },
  computed: {
    ...mapGetters([
      'distributors',
      'skuList',
      'codes'
    ]),
    nameErrors () {
      const errors = []
      if (!this.$v.name.$dirty) return errors
      !this.$v.name.maxLength && errors.push('Name must be at most 30 characters long')
      !this.$v.name.required && errors.push('Name is required')
      return errors
    },
    distributorErrors () {
      const errors = []
      if (!this.$v.distributor.$dirty) return errors
      !this.$v.distributor.required && errors.push('Distributor is required')
      return errors
    },
    skuErrors () {
      const errors = []
      if (!this.$v.sku.$dirty) return errors
      !this.$v.sku.required && errors.push('Sku is required')
      return errors
    },
    countErrors () {
      const errors = []
      if (!this.$v.count.$dirty) return errors
      !this.$v.count.required && errors.push('Count is required')
      !this.$v.count.numeric && errors.push('Count must be numeric')
      !this.$v.count.minValue && errors.push('Count min value 1')
      !this.$v.count.maxValue && errors.push('Count max value 20000')
      return errors
    },
    orderErrors () {
      const errors = []
      if (!this.$v.order.$dirty) return errors
      !this.$v.order.required && errors.push('Order is required')
      !this.$v.order.numeric && errors.push('Order must be numeric')
      !this.$v.order.minValue && errors.push('Order min value 1')
      !this.$v.order.maxValue && errors.push('Order max value 20000')
      return errors
    },
    blocksCountErrors () {
      const errors = []
      if (!this.$v.blocksCount.$dirty) return errors
      !this.$v.blocksCount.required && errors.push('Blocks is required')
      return errors
    },
    skuBlocksCount () {
      const sku = this.skuList.filter(item => this.sku === item.id)
      if (sku && sku[0]) {
        return sku[0].count
      }
      return 0
    }
  },
  created: async function () {
    await this.$store.dispatch(DISTRIBS_LIST)
    await this.$store.dispatch(SKU_LIST)
  },
  mounted: async function () {
    this.distributor = this.distributorInit
  },
  watch: {
    init: async function (newOne) {
      if (newOne && newOne.name) {
        this.order = parseInt(await api.getMaxOrder(newOne.id)) + 1
        this.name = newOne.name
        this.sku = newOne.sku.id
        this.distributor = newOne.distributor.id
        this.count = newOne.count
        this.blocksCount = newOne.blocksCount
        this.isUpdate = true
      }
    },
    distributor: async function () {
      this.setName()
      if (this.distributor) {
        this.order = parseInt(await api.getMaxOrder(this.distributor)) + 1
      }
    },
    distributorInit: function () {
      this.distributor = this.distributorInit
    },
    sku: function () {
      this.setName()
      if (this.skuBlocksCount) {
        this.blocksCount = this.skuBlocksCount
      }
    },
    count: function () {
      this.setName()
    },
    order: function () {
      this.setName()
    }
  }
}
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/components/Codes/Dialog.vue